import {
  // @ts-ignore
  external,
  getToolState,
  addToolState,
  toolColors,
  BaseToolConfiguration,
  AnnotationHandle,
} from 'cornerstone-tools';
import * as _ from 'lodash';
import { BaseAnnotationTool, draw, drawHandles, getNewContext } from 'app/CornerstoneTools';
import { offsetPixelPointInCanvas } from './pixelToCanvasUtils';
import { IntlShape } from 'react-intl';
import {
  EventWithCornerstoneDetail,
  ImageMarkerAnnotation,
  Point,
  RenderEventWithCornerstoneDetail,
} from 'app/CornerstoneTools/types';

type ImageMarkerToolProps = {
  configuration?: BaseToolConfiguration;
  markerStore?: any;
  getIntl?: () => IntlShape;
};

/**
 * @public
 * @class ImageMarkerTool
 * @memberof Tools.Annotation
 *
 * @classdesc Tool for annotating an image with image markers.
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class ImageMarkerTool extends BaseAnnotationTool {
  markerStore: any;
  getIntl: () => IntlShape;
  constructor(props: ImageMarkerToolProps = {}) {
    const defaultProps = {
      name: 'ImageMarker',
      supportedInteractionTypes: ['Mouse', 'Touch'],
      configuration: {
        allowMultiple: false,
        initialImageWidth: 40,
        initialImageHeight: 40,
        drawHandlesOnHover: true,
      },
    };
    const { markerStore, getIntl } = props;
    const svgCursor = markerStore.get(getIntl().locale).markerCursor;
    super({ ...props, svgCursor }, defaultProps);
    this.markerStore = markerStore;
    this.getIntl = getIntl;
  }

  createNewMeasurement = (eventData: EventWithCornerstoneDetail['detail']) => {
    const config = this.configuration;
    const { allowMultiple, initialImageHeight, initialImageWidth } = config;

    const toolData = getToolState(eventData.element, this.name);

    if (_.get(toolData, 'data', []).length > 0 && !allowMultiple) {
      return null;
    }

    const bottomRight = {
      x: eventData.currentPoints.image.x,
      y: eventData.currentPoints.image.y,
      highlight: true,
      active: false,
      allowedOutsideImage: true,
    };
    const topLeft: AnnotationHandle = offsetPixelPointInCanvas(eventData.element, bottomRight, {
      x: -initialImageWidth,
      y: -initialImageHeight,
    });
    topLeft.highlight = true;
    topLeft.active = false;
    topLeft.allowedOutsideImage = true;

    // Create the measurement data for this tool with the end handle activated
    const measurementData: ImageMarkerAnnotation = {
      visible: true,
      active: false,
      color: undefined,
      handles: {
        topLeft,
        bottomRight,
      },
    };

    return measurementData;
  };

  addNewMeasurement = (evt: EventWithCornerstoneDetail) => {
    evt.preventDefault();
    evt.stopPropagation();
    const eventData = evt.detail;
    const { element } = eventData;
    const measurementData = this.createNewMeasurement(eventData);

    if (!measurementData) {
      return;
    }

    addToolState(element, this.name, measurementData);

    external.cornerstone.updateImage(element);
  };

  // eslint-disable-next-line class-methods-use-this
  pointNearTool(element: HTMLElement, data: ImageMarkerAnnotation, coords: Point) {
    if (data.visible === false) {
      return false;
    }

    const rect = {
      left: data.handles.topLeft.x,
      top: data.handles.topLeft.y,
      width: data.handles.bottomRight.x - data.handles.topLeft.x,
      height: data.handles.bottomRight.y - data.handles.topLeft.y,
    };
    const pixelCoords = external.cornerstone.canvasToPixel(element, coords);
    const distanceToPoint = external.cornerstoneMath.rect.distanceToPoint(rect, pixelCoords);
    const insideRect = external.cornerstoneMath.point.insideRect(pixelCoords, rect);

    return distanceToPoint < 10 || insideRect;
  }

  // eslint-disable-next-line class-methods-use-this
  updateCachedStats() {
    // Implementing to satisfy BaseAnnotationTool
  }

  renderToolData(evt: RenderEventWithCornerstoneDetail) {
    const eventData = evt.detail;
    const config = this.configuration;
    const { markerStore, getIntl } = this;
    const { drawHandlesOnHover, handleRadius } = config;
    const markerImage = markerStore.get(getIntl().locale).img;
    // If we have no toolData for this element, return immediately as there is nothing to do
    const toolData = getToolState(eventData.element, this.name);

    if (!toolData) return;
    if (!markerImage) return;

    // We have tool data for this element - iterate over each one and draw it
    const canvasContext = getNewContext(eventData.canvasContext.canvas);

    for (let i = 0; i < toolData.data.length; i += 1) {
      const data = toolData.data[i];

      if (data.visible === false) {
        continue;
      }
      const color = toolColors.getColorIfActive(data);

      draw(canvasContext, (context: CanvasRenderingContext2D) => {
        const imageStartCanvasCoords = external.cornerstone.pixelToCanvas(
          eventData.element,
          data.handles.topLeft
        );
        const imageEndCanvasCoords = external.cornerstone.pixelToCanvas(
          eventData.element,
          data.handles.bottomRight
        );
        const width = imageEndCanvasCoords.x - imageStartCanvasCoords.x;
        const height = imageEndCanvasCoords.y - imageStartCanvasCoords.y;

        context.drawImage(
          markerImage,
          imageStartCanvasCoords.x,
          imageStartCanvasCoords.y,
          width,
          height
        );

        const handleOptions = {
          color,
          handleRadius,
          drawHandlesIfActive: drawHandlesOnHover && !data.active,
          hideHandlesIfMoving: false,
        };

        drawHandles(context, eventData, data.handles, handleOptions);
      });
    }
  }
}
